import React, { useState } from "react";
import { Toolbar, Box, IconButton, Typography, Button, Slide, Menu, MenuItem, alpha } from "@mui/material";
import { Close, Download, Delete, MoreVert, Edit, Share } from "@mui/icons-material";

interface MultiSelectToolbarProps {
  multiSelected: string[] | null;
  onClose: () => void;
  onDownload: () => void;
  onRename: () => void;
  onDelete: () => void;
}

export default function MultiSelectToolbar({
  multiSelected,
  onClose,
  onDownload,
  onRename,
  onDelete,
}: MultiSelectToolbarProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const selectedCount = multiSelected?.length || 0;
  const isSingleFile = selectedCount === 1 && !multiSelected?.[0].endsWith("/");

  return (
    <Slide direction="up" in={multiSelected !== null}>
      <Toolbar
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 -1px 2px 0 rgba(60,64,67,0.3), 0 -2px 6px 2px rgba(60,64,67,0.15)',
          minHeight: { xs: 64, sm: 72 },
          gap: { xs: 1, sm: 2 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
          <IconButton
            onClick={onClose}
            size="medium"
            sx={{
              color: 'text.secondary',
            }}
          >
            <Close />
          </IconButton>
          <Typography variant="body1" sx={{ fontWeight: 400, fontSize: '0.875rem' }}>
            {selectedCount} selected
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            disabled={!isSingleFile}
            onClick={onDownload}
            size="medium"
            sx={{
              color: 'text.secondary',
              '&:disabled': {
                color: 'action.disabled',
              },
            }}
          >
            <Download fontSize="small" />
          </IconButton>

          <IconButton
            onClick={onDelete}
            size="medium"
            sx={{
              color: 'text.secondary',
            }}
          >
            <Delete fontSize="small" />
          </IconButton>

          <IconButton
            disabled={!isSingleFile}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            size="medium"
            sx={{
              color: 'text.secondary',
              '&:disabled': {
                color: 'action.disabled',
              },
            }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>

        {multiSelected?.length && (
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            slotProps={{
              paper: {
                sx: {
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  minWidth: 180,
                }
              }
            }}
          >
            {multiSelected.length === 1 && (
              <React.Fragment>
                <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    onRename();
                  }}
                  sx={{ gap: 2 }}
                >
                  <Edit fontSize="small" />
                  Rename
                </MenuItem>
                <MenuItem onClick={() => setAnchorEl(null)} sx={{ gap: 2 }}>
                  <Share fontSize="small" />
                  Share
                </MenuItem>
              </React.Fragment>
            )}
          </Menu>
        )}
      </Toolbar>
    </Slide>
  );
}
